import { useState, useEffect } from "react";
import { useInView } from "react-intersection-observer";
import { ChatWidget } from "@/components/landing2/chat/ChatWidget";

type Punchline = {
  pre: string;
  post: string;
  sub: string;
};

const APP_TYPES = [
  "an AI CRM",
  "an AI chat app",
  "an internal tool",
  "a REST API",
  "a marketplace",
  "a Next.js app",
  "a Telegram bot",
  "a side project"
];

const LANDING_CTA_ACTIONS = [
  { label: "Create account & deploy →", href: "/dashboard", variant: "primary" as const },
  { label: "See the plan first", href: "/pricing", variant: "secondary" as const }
];

const useStableTypewriter = (words: string[], active: boolean) => {
  const [text, setText] = useState(words[0] ?? "");
  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState<"typing" | "hold" | "erasing">("hold");

  useEffect(() => {
    if (!words.length || !active) {
      return;
    }
    const word = words[idx % words.length];
    let t: ReturnType<typeof setTimeout>;
    if (phase === "typing") {
      if (text.length < word.length) {
        t = setTimeout(() => setText(word.slice(0, text.length + 1)), 60);
      } else {
        t = setTimeout(() => setPhase("hold"), 1600);
      }
    } else if (phase === "hold") {
      t = setTimeout(() => setPhase("erasing"), 1600);
    } else {
      if (text.length > 0) {
        t = setTimeout(() => setText(text.slice(0, -1)), 28);
      } else {
        setIdx(x => x + 1);
        setPhase("typing");
      }
    }
    return () => clearTimeout(t);
  }, [text, phase, idx, words, active]);

  return text;
};

export const Hero = ({ punchline }: { punchline: Punchline }) => {
  const { ref, inView } = useInView();
  const typed = useStableTypewriter(APP_TYPES, inView);

  return (
    <section className="relative z-[1] px-6 pt-16 pb-22">
      <div className="max-w-[920px] mx-auto text-center">
        <div
          className="inline-flex items-center gap-2 text-xs text-muted-foreground bg-foreground/[0.04] border rounded-full px-3.5 py-1.5 mb-8 font-medium"
          aria-label="For developers, founders and indie hackers"
        >
          <span
            className="w-1.5 h-1.5 rounded-full bg-primary shadow-[0_0_0_3px_oklch(0.569_0.195_269_/_0.18)]"
            aria-hidden="true"
          />
          for developers, founders &amp; indie hackers
        </div>

        <h1 className="font-serif font-normal text-[clamp(44px,7vw,76px)] leading-[1.04] tracking-tight mb-5 text-foreground text-balance">
          <div>{punchline.pre}</div>{" "}
          <span
            className="inline align-baseline font-serif italic text-primary"
            aria-live="polite"
            aria-label={`"${typed}"`}
          >
            <span className="text-muted-foreground/60 not-italic" aria-hidden="true">
              &ldquo;
            </span>
            <span className="italic" ref={ref}>
              {typed}
            </span>
            <span
              className="inline-block ml-0.5 text-primary not-italic font-light animate-caret-blink"
              aria-hidden="true"
            >
              |
            </span>
            <span className="text-muted-foreground/60 not-italic" aria-hidden="true">
              &rdquo;
            </span>
          </span>
          <br />
          <span>{punchline.post}</span>
        </h1>

        <p className="text-lg leading-relaxed text-muted-foreground max-w-[560px] mx-auto mb-10 text-pretty">
          {punchline.sub}
        </p>

        <ChatWidget ctaActions={LANDING_CTA_ACTIONS} trackPrefix="L2" glow />

        <div
          className="flex justify-center items-center gap-3 mt-9 flex-wrap"
          aria-label="Trust indicators"
        >
          <span className="text-xs text-muted-foreground/60">start free</span>
          <span className="w-1.5 h-1.5 rounded-full bg-primary" aria-hidden="true" />
          <span className="text-xs text-muted-foreground/60">14-day free trial</span>
          <span className="w-1.5 h-1.5 rounded-full bg-primary" aria-hidden="true" />
          <span className="text-xs text-muted-foreground/60">servers from $4/mo</span>
          <span className="w-1.5 h-1.5 rounded-full bg-primary" aria-hidden="true" />
          <span className="text-xs text-muted-foreground/60">no lock-in</span>
        </div>
      </div>
    </section>
  );
};
